import React, { useState, useCallback, useRef } from 'react';
import './RouletteButton.css';
import { API_URL } from '../config';

/**
 * RouletteButton
 * Picks a random community design and calls onSelect(design, dominantColors).
 * Also calls onFlashColors(colors) so the parent (Hero) can animate its background.
 */
function RouletteButton({ onSelect, onFlashColors }) {
  const [spinning, setSpinning] = useState(false);
  const [label, setLabel] = useState('🎰 Surprise Me');
  const debounceRef = useRef(null);

  // Extract 2 dominant colors from an image URL via canvas
  const extractColors = useCallback(async (imageUrl) => {
    return new Promise((resolve) => {
      try {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = 80;
            canvas.height = 80;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, 80, 80);
            const { data } = ctx.getImageData(0, 0, 80, 80);
            // Sample every 16th pixel, bucket by 32
            const buckets = {};
            for (let i = 0; i < data.length; i += 16) {
              const r = Math.round(data[i] / 48) * 48;
              const g = Math.round(data[i + 1] / 48) * 48;
              const b = Math.round(data[i + 2] / 48) * 48;
              if (data[i + 3] < 128) continue; // skip transparent
              const key = `${r},${g},${b}`;
              buckets[key] = (buckets[key] || 0) + 1;
            }
            const sorted = Object.entries(buckets)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3)
              .map(([k]) => {
                const [r, g, b] = k.split(',').map(Number);
                return `rgb(${r},${g},${b})`;
              });
            resolve(sorted.length ? sorted : ['#8b5cf6', '#ec4899']);
          } catch {
            resolve(['#8b5cf6', '#ec4899']);
          }
        };
        img.onerror = () => resolve(['#8b5cf6', '#ec4899']);
        img.src = imageUrl;
      } catch {
        resolve(['#8b5cf6', '#ec4899']);
      }
    });
  }, []);

  const handleClick = useCallback(async () => {
    if (spinning) return;

    // Debounce rapid clicks
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {}, 300);

    setSpinning(true);
    setLabel('Spinning…');

    try {
      // Fetch a pool of recent designs
      const res = await fetch(`${API_URL}/designs?limit=50&sort=recent`);
      if (!res.ok) throw new Error('Failed to fetch designs');
      const { designs } = await res.json();

      // Filter to valid designs (real images, not placeholders)
      const valid = (designs || []).filter(
        (d) =>
          d.image_url &&
          !d.image_url.includes('picsum') &&
          (d.image_url.startsWith('https://') || d.image_url.startsWith('data:'))
      );

      if (!valid.length) {
        setLabel('No designs yet!');
        setTimeout(() => setLabel('🎰 Surprise Me'), 2000);
        setSpinning(false);
        return;
      }

      // Pick random
      const picked = valid[Math.floor(Math.random() * valid.length)];

      // Extract colors for background flash
      let colors = ['#8b5cf6', '#ec4899'];
      try {
        colors = await extractColors(picked.image_url);
      } catch {}

      // Animate spin for at least 800ms for drama
      await new Promise((r) => setTimeout(r, 800));

      setSpinning(false);
      setLabel('🎰 Surprise Me');

      // Notify parent
      onFlashColors?.(colors);
      onSelect?.(picked, colors);
    } catch (err) {
      console.error('Roulette failed:', err);
      setSpinning(false);
      setLabel('🎰 Try again');
      setTimeout(() => setLabel('🎰 Surprise Me'), 2000);
    }
  }, [spinning, extractColors, onSelect, onFlashColors]);

  return (
    <button
      className="roulette-btn"
      onClick={handleClick}
      disabled={spinning}
      title="Pick a random community design and load it into the creator"
      aria-label="Random Design Roulette"
    >
      <span className={`roulette-wheel${spinning ? ' roulette-wheel--spinning' : ''}`}>
        🎡
      </span>
      {label}
    </button>
  );
}

export default RouletteButton;
